import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
    tryToFetchBrandingConfig,
    tryToPatchBrandingConfig,
} from './BrandingApi';

type BrandingConfigState = {
    isLoading: boolean;
    isSaving: boolean;
    config: any | null;
    originalConfig: any | null;
    error: string | null;
    success: boolean;
};

const initialState: BrandingConfigState = {
    isLoading: false,
    isSaving: false,
    config: null,
    originalConfig: null,
    error: null,
    success: false,
};

export const fetchBrandingConfig = createAsyncThunk(
    'brandingConfig/fetch',
    async (
        { hotelUsername, type }: { hotelUsername: string; type: string },
        { rejectWithValue },
    ) => {
        try {
            const res = await tryToFetchBrandingConfig(hotelUsername, type);
            return res.data.metadata;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const patchBrandingConfig = createAsyncThunk(
    'brandingConfig/patch',
    async (
        {
            hotelUsername,
            type,
            data,
        }: { hotelUsername: string; type: string; data: any },
        { rejectWithValue },
    ) => {
        try {
            const res = await tryToPatchBrandingConfig(
                hotelUsername,
                type,
                data,
            );
            return res.data.metadata;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const brandingConfigSlice = createSlice({
    name: 'brandingConfig',
    initialState,
    reducers: {
        setConfig(state, action) {
            state.config = action.payload;
        },
        setOriginalConfig(state, action) {
            state.originalConfig = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBrandingConfig.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchBrandingConfig.fulfilled, (state, action) => {
                state.isLoading = false;
                state.config = action.payload;
                state.originalConfig = action.payload;
                state.success = false;
            })
            .addCase(fetchBrandingConfig.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            .addCase(patchBrandingConfig.pending, (state) => {
                state.isSaving = true;
                state.success = false;
            })
            .addCase(patchBrandingConfig.fulfilled, (state, action) => {
                state.isSaving = false;
                state.config = action.payload;
                state.originalConfig = action.payload;
                state.success = true;
            })
            .addCase(patchBrandingConfig.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload as string;
            });
    },
});

export const { setConfig, setOriginalConfig } = brandingConfigSlice.actions;
export default brandingConfigSlice.reducer;
